import React, { ReactNode, createContext, useContext, useState } from 'react'

import { DocumentTypeOption, OtherDocumentType } from '@documents/types'

type DocumentsFlowContextValue = {
  selectedDocumentType: DocumentTypeOption | null
  selectedOtherDocumentTypes: OtherDocumentType[]
  updateSelectedDocumentType: (value: DocumentTypeOption | null) => void
  updateSelectedOtherDocumentTypes: (value: OtherDocumentType) => void
  clearOtherDocumentTypeSelections: () => void
}

const DocumentsFlowContext = createContext<DocumentsFlowContextValue | null>(null)

type Props = {
  children: ReactNode
}

export function DocumentsFlowProvider({ children }: Props) {
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentTypeOption | null>(null)
  const [selectedOtherDocumentTypes, setSelectedOtherDocumentTypes] = useState<OtherDocumentType[]>([])

  function updateSelectedDocumentType(value: DocumentTypeOption | null) {
    setSelectedDocumentType(value)
  }

  function updateSelectedOtherDocumentTypes(value: OtherDocumentType) {
    setSelectedOtherDocumentTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value)
      }

      return [...prev, value]
    })
  }

  function clearOtherDocumentTypeSelections() {
    setSelectedOtherDocumentTypes([])
  }

  return (
    <DocumentsFlowContext.Provider
      value={{
        selectedDocumentType,
        selectedOtherDocumentTypes,
        updateSelectedDocumentType,
        updateSelectedOtherDocumentTypes,
        clearOtherDocumentTypeSelections,
      }}
    >
      {children}
    </DocumentsFlowContext.Provider>
  )
}

export function useDocumentsFlowContext() {
  const context = useContext(DocumentsFlowContext)

  if (!context) {
    throw new Error('useDocumentsFlowContext must be used within a DocumentsFlowProvider')
  }

  return context
}
